import { useTranslations } from 'next-intl';

import { Button } from '@/components/button';
import { useLeaderboardWallet } from '@/hooks/api/quanta/useLeaderboardWallet';
import { useQuantaVaultLogin } from '@/hooks/api/quanta/useQuantaVaultLogin';

import { useQuantaMigration } from '../context/QuantaMigrationState';

export default function ConnectedWallet({ userId }: { userId: string }) {
  const t = useTranslations('quanta.connectedWallet');
  const migration = useQuantaMigration();
  const flow = useQuantaVaultLogin(userId, 'payment', { autoMatch: !migration?.active });
  const vaultIdentity = useQuantaVaultLogin(userId, 'vault');
  const display = useLeaderboardWallet(userId);
  const displayError = display.paymentSession && display.connected.isError;
  const conflict =
    flow.conflict || vaultIdentity.conflict || flow.error === 'VAULT_LOGIN_TARGET_IN_USE' || flow.error === 'QUANTA_ACCOUNT_CONFLICT';
  if (!flow.needed && !conflict && !flow.error && !flow.checkError && !displayError) return null;
  return (
    <section className="rounded-lg border border-border-primary bg-surface-primary p-4 text-sm text-text-primary" aria-busy={flow.busy}>
      <p className="font-medium">{t(conflict ? 'conflictTitle' : displayError ? 'displayTitle' : 'title')}</p>
      {!conflict && !displayError && <p className="mt-1 text-text-secondary">{t('description')}</p>}
      {(conflict || flow.error || flow.checkError || displayError) && (
        <p role="alert" className="mt-2 text-text-secondary">
          {t(conflict ? 'conflict' : flow.error === 'ACCOUNT_AUTHORITY_REQUIRED' ? 'authority' : displayError ? 'displayError' : 'failed')}
        </p>
      )}
      {flow.busy && (
        <p role="status" className="mt-2">
          {t('approving')}
        </p>
      )}
      {!conflict && (
        <Button
          className="mt-3"
          variant="secondary"
          size="small"
          loading={flow.busy || flow.checking || display.connected.isFetching}
          disabled={flow.busy || flow.checking || display.connected.isFetching}
          onClick={() =>
            void (displayError
              ? display.connected.refetch()
              : flow.checkError
                ? flow.retryCheck()
                : flow.recovery
                  ? flow.recover()
                  : flow.begin())
          }
        >
          {t(displayError || flow.checkError ? 'retry' : 'action')}
        </Button>
      )}
    </section>
  );
}
